/**
 * This device's identity for the support desk (OD-65): a NON-EXTRACTABLE ECDSA P-256 key created by
 * WebCrypto and kept in IndexedDB (the browser stores the key object; JavaScript can sign with it but can
 * never read or export the private part). Only the device id and the key handle live here — no
 * session token (that is an HttpOnly cookie) and no personal data.
 *
 * On iPhone the home-screen app has its own storage, so pairing must happen INSIDE the installed app.
 */
const DB = 'hopex-desk';
const STORE = 'device';
const KEY = 'current';

export interface StoredDevice {
  deviceId: string;
  key: CryptoKey;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('indexeddb'));
  });
}

async function tx<T>(
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const t = db.transaction(STORE, mode);
      const req = fn(t.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('indexeddb'));
    });
  } finally {
    db.close();
  }
}

export async function loadDevice(): Promise<StoredDevice | null> {
  try {
    const v = (await tx('readonly', (s) => s.get(KEY))) as StoredDevice | undefined;
    return v && typeof v.deviceId === 'string' && v.key ? v : null;
  } catch {
    return null;
  }
}

export async function forgetDevice(): Promise<void> {
  try {
    await tx('readwrite', (s) => s.delete(KEY));
  } catch {
    /* nothing stored */
  }
}

const b64u = (buf: ArrayBuffer): string => {
  let s = '';
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** A fresh key pair for pairing; the private key is non-extractable from the start. */
export async function createDeviceKey(): Promise<{ key: CryptoKey; publicKey: string }> {
  const pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, [
    'sign',
    'verify',
  ]);
  const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
  return { key: pair.privateKey, publicKey: b64u(spki) };
}

export async function saveDevice(d: StoredDevice): Promise<void> {
  await tx('readwrite', (s) => s.put(d, KEY));
}

/** ECDSA P-256 / SHA-256 signature (IEEE P1363 r||s — what WebCrypto returns), base64url. */
export async function signChallenge(key: CryptoKey, challenge: string): Promise<string> {
  const sig = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    key,
    new TextEncoder().encode(challenge),
  );
  return b64u(sig);
}
